/**
 * Cities feature — data layer.
 *
 * TanStack Query hooks for the public /city/:country/:slug surface and its
 * five panels. Mirrors `useEvents.ts` for pattern consistency.
 *
 * All endpoints hang off `/api/cities` (see
 * `nodejs_server/controllers/CityController.js`). Response envelopes are
 * unwrapped by the API client, so hooks return the inner payload directly.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import type {
  CitiesIndexResult,
  City,
  CityBlogCard,
  CityCreatorCard,
  CityDetailResult,
  CityEventCard,
  CityFollowResult,
  CityLivestreamCard,
  CityPostCard,
  CityWithDistance,
  PlaceEvent,
} from '@/types/api';

/** 5 min — matches the server-side cache window on these endpoints. */
const STATIC_STALE = 5 * 60 * 1000;
/** 60 s — for lists that turn over faster (posts, livestreams). */
const LIVE_STALE = 60 * 1000;

interface IndexParams {
  country?: string;
  sort?: 'activity' | 'name' | 'users';
  page?: number;
  limit?: number;
}

function toQuery(params: Record<string, unknown> = {}): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    search.set(k, String(v));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/** GET /api/cities[/:country] — paginated directory. */
export function useCities(params: IndexParams = {}) {
  const { country, ...rest } = params;
  const path = country ? `cities/${encodeURIComponent(country)}` : 'cities';
  return useQuery({
    queryKey: queryKeys.cities.index(country, rest),
    staleTime: STATIC_STALE,
    queryFn: () => api.get<CitiesIndexResult>(`${path}${toQuery(rest)}`),
  });
}

/** GET /api/cities/resolve/:slug — bare-slug resolver.
 *
 *  Returns the top-ranked (country_slug, slug) tuple for a city whose
 *  slug matches `slug`. Powers the legacy `/city/:slug` client-side
 *  route in dev (where the Node SSR 301 doesn't fire because Vite
 *  serves the SPA shell directly). On 404 the hook's `error` is
 *  populated and the caller renders NotFound. */
export function useResolveCitySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ['cities', 'resolve', slug ?? ''],
    enabled: !!slug,
    staleTime: STATIC_STALE,
    retry: false,
    queryFn: () =>
      api.get<{ city: { country_slug: string; slug: string; url: string } }>(
        `cities/resolve/${encodeURIComponent(slug!)}`,
      ).then((r) => r.city),
  });
}

/** GET /api/cities/:country/:slug — city metadata + stats (+ `is_following`
 *  for signed-in viewers). */
export function useCity(country: string | undefined, slug: string | undefined) {
  const enabled = !!country && !!slug;
  return useQuery({
    queryKey: queryKeys.cities.detail(country ?? '', slug ?? ''),
    enabled,
    staleTime: STATIC_STALE,
    queryFn: () =>
      api
        .get<CityDetailResult>(
          `cities/${encodeURIComponent(country!)}/${encodeURIComponent(slug!)}`,
        )
        .then((r) => r.city),
  });
}

/** POST /api/cities/:country/:slug/follow — mark the viewer as following
 *  the city. Optimistically flips `is_following` on the cached city row
 *  before the request lands so the Follow button toggles instantly. */
export function useFollowCity(
  country: string | undefined,
  slug: string | undefined,
) {
  const qc = useQueryClient();
  const key = queryKeys.cities.detail(country ?? '', slug ?? '');
  return useMutation({
    mutationFn: () =>
      api.post<CityFollowResult>(
        `cities/${encodeURIComponent(country!)}/${encodeURIComponent(slug!)}/follow`,
        {},
      ),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<CityDetailResult['city']>(key);
      if (prev) {
        // Optimistic flip + user_count +1. If the server returns an
        // error, onError below restores the old row.
        qc.setQueryData(key, {
          ...prev,
          is_following: true,
          user_count: (prev.user_count ?? 0) + (prev.is_following ? 0 : 1),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
    },
  });
}

/** POST /api/cities/:country/:slug/unfollow — mirror of `useFollowCity`. */
export function useUnfollowCity(
  country: string | undefined,
  slug: string | undefined,
) {
  const qc = useQueryClient();
  const key = queryKeys.cities.detail(country ?? '', slug ?? '');
  return useMutation({
    mutationFn: () =>
      api.post<CityFollowResult>(
        `cities/${encodeURIComponent(country!)}/${encodeURIComponent(slug!)}/unfollow`,
        {},
      ),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<CityDetailResult['city']>(key);
      if (prev) {
        qc.setQueryData(key, {
          ...prev,
          is_following: false,
          user_count: Math.max(0, (prev.user_count ?? 0) - (prev.is_following ? 1 : 0)),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
    },
  });
}

interface PanelOpts {
  limit?: number;
  enabled?: boolean;
}

/** GET /api/cities/:country/:slug/events — upcoming events.
 *
 *  The server reuses `EventsController._shapeEvent`, so the response is
 *  shaped identically to `/api/events/discover` — same `PlaceEvent`
 *  objects, same `url`, same `counts`, same `creator` block. The city
 *  page can render each with the shared `<EventCard>`. */
export function useCityEvents(
  country: string | undefined,
  slug: string | undefined,
  { limit = 12, enabled = true }: PanelOpts = {},
) {
  const can = !!country && !!slug && enabled;
  return useQuery({
    queryKey: queryKeys.cities.events(country ?? '', slug ?? '', limit),
    enabled: can,
    staleTime: STATIC_STALE,
    queryFn: () =>
      api
        .get<{ list: PlaceEvent[]; total: number }>(
          `cities/${encodeURIComponent(country!)}/${encodeURIComponent(slug!)}/events?limit=${limit}`,
        )
        .then((r) => r.list),
  });
}

/** GET /api/cities/:country/:slug/creators — top creators (14-day activity). */
export function useCityCreators(
  country: string | undefined,
  slug: string | undefined,
  { limit = 12, enabled = true }: PanelOpts = {},
) {
  const can = !!country && !!slug && enabled;
  return useQuery({
    queryKey: queryKeys.cities.creators(country ?? '', slug ?? '', limit),
    enabled: can,
    staleTime: STATIC_STALE,
    queryFn: () =>
      api
        .get<{ list: CityCreatorCard[] }>(
          `cities/${encodeURIComponent(country!)}/${encodeURIComponent(slug!)}/creators?limit=${limit}`,
        )
        .then((r) => r.list),
  });
}

/** GET /api/cities/:country/:slug/posts — trending public posts (7-day). */
export function useCityPosts(
  country: string | undefined,
  slug: string | undefined,
  { limit = 20, enabled = true }: PanelOpts = {},
) {
  const can = !!country && !!slug && enabled;
  return useQuery({
    queryKey: queryKeys.cities.posts(country ?? '', slug ?? '', limit),
    enabled: can,
    staleTime: LIVE_STALE,
    queryFn: () =>
      api
        .get<{ list: CityPostCard[] }>(
          `cities/${encodeURIComponent(country!)}/${encodeURIComponent(slug!)}/posts?limit=${limit}`,
        )
        .then((r) => r.list),
  });
}

/** GET /api/cities/:country/:slug/blogs — recent blogs by city users. */
export function useCityBlogs(
  country: string | undefined,
  slug: string | undefined,
  { limit = 10, enabled = true }: PanelOpts = {},
) {
  const can = !!country && !!slug && enabled;
  return useQuery({
    queryKey: queryKeys.cities.blogs(country ?? '', slug ?? '', limit),
    enabled: can,
    staleTime: STATIC_STALE,
    queryFn: () =>
      api
        .get<{ list: CityBlogCard[] }>(
          `cities/${encodeURIComponent(country!)}/${encodeURIComponent(slug!)}/blogs?limit=${limit}`,
        )
        .then((r) => r.list),
  });
}

/** GET /api/cities/:country/:slug/livestreams — live or upcoming. */
export function useCityLivestreams(
  country: string | undefined,
  slug: string | undefined,
  { limit = 6, enabled = true }: PanelOpts = {},
) {
  const can = !!country && !!slug && enabled;
  return useQuery({
    queryKey: queryKeys.cities.livestreams(country ?? '', slug ?? '', limit),
    enabled: can,
    staleTime: LIVE_STALE,
    queryFn: () =>
      api
        .get<{ list: CityLivestreamCard[] }>(
          `cities/${encodeURIComponent(country!)}/${encodeURIComponent(slug!)}/livestreams?limit=${limit}`,
        )
        .then((r) => r.list),
  });
}

/** GET /api/cities/:country/:slug/nearby — same-country closest by haversine. */
export function useNearbyCities(
  country: string | undefined,
  slug: string | undefined,
  { limit = 10, enabled = true }: PanelOpts = {},
) {
  const can = !!country && !!slug && enabled;
  return useQuery({
    queryKey: queryKeys.cities.nearby(country ?? '', slug ?? '', limit),
    enabled: can,
    staleTime: STATIC_STALE,
    queryFn: () =>
      api
        .get<{ list: CityWithDistance[] }>(
          `cities/${encodeURIComponent(country!)}/${encodeURIComponent(slug!)}/nearby?limit=${limit}`,
        )
        .then((r) => r.list),
  });
}

/** Named re-exports so the barrel can surface them in one block. */
export type {
  City,
  CityWithDistance,
  CityEventCard,
  CityCreatorCard,
  CityPostCard,
  CityBlogCard,
  CityLivestreamCard,
};
